// Public reset-password page reached from the emailed link: /reset-password?token=...
import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { KeyRound, Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ACCENT = "#C75A1B";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const { resetPassword } = useAuth();
    const navigate = useNavigate();
    const [nw, setNw] = useState("");
    const [cf, setCf] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setErr("");
        if (nw.length < 8) { setErr("Password must be at least 8 characters."); return; }
        if (nw !== cf) { setErr("Passwords don't match."); return; }
        setBusy(true);
        const res = await resetPassword(token, nw);
        setBusy(false);
        if (res.ok) { setDone(true); setTimeout(() => navigate("/login", { replace: true }), 2500); }
        else setErr(res.error || "Could not reset password");
    }

    const field = "w-full bg-white border rounded-[10px] px-3.5 py-2.5 text-[13px] text-[#1F1F1F] outline-none focus:border-[#C75A1B] focus:ring-2 focus:ring-[#FBEFE5]";
    const lbl = "block text-[11px] font-semibold uppercase tracking-wide mb-1.5";

    return (
        <div className="min-h-screen w-full grid place-items-center bg-[#F5F3F0] p-6" data-testid="reset-password-page">
            <div className="w-full max-w-[420px] bg-white border rounded-[16px] shadow-brand p-7" style={{ borderColor: "#E5E0DA" }}>
                <h1 className="font-display text-2xl font-bold text-[#1F1F1F] mb-1">Choose a new password</h1>
                {!token ? (
                    <p className="text-[13px] mt-2" style={{ color: "#B33A2E" }}>This reset link is missing its token. Please use the link from your email, or request a new one.</p>
                ) : done ? (
                    <div className="mt-4 text-center" data-testid="reset-done">
                        <CheckCircle2 className="w-10 h-10 mx-auto mb-2" style={{ color: "#3F7D3A" }} />
                        <p className="text-[13px]" style={{ color: "#4a4641" }}>Password reset. Redirecting you to sign in…</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3.5 mt-4">
                        <div>
                            <span className={lbl} style={{ color: "#6F6A64" }}>New password</span>
                            <div className="relative">
                                <input data-testid="reset-new" type={show ? "text" : "password"} value={nw} onChange={(e) => setNw(e.target.value)} required autoComplete="new-password" className={field} />
                                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2" tabIndex={-1}>
                                    {show ? <EyeOff className="w-4 h-4 text-[#9A968F]" /> : <Eye className="w-4 h-4 text-[#9A968F]" />}
                                </button>
                            </div>
                        </div>
                        <div>
                            <span className={lbl} style={{ color: "#6F6A64" }}>Confirm password</span>
                            <input data-testid="reset-confirm" type={show ? "text" : "password"} value={cf} onChange={(e) => setCf(e.target.value)} required autoComplete="new-password" className={field} />
                        </div>
                        {err && <div data-testid="reset-error" className="px-3.5 py-2.5 rounded-[10px] text-[12.5px]" style={{ background: "#FBE8E5", color: "#B33A2E", border: "1px solid #F2D0CA" }}>{err}</div>}
                        <button type="submit" data-testid="reset-submit" disabled={busy}
                            className="w-full py-3 rounded-[10px] text-[13px] font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: ACCENT }}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Reset password
                        </button>
                    </form>
                )}
                <div className="mt-5 text-center">
                    <Link to="/login" className="text-[12px] font-semibold hover:underline" style={{ color: ACCENT }}>Back to sign in</Link>
                </div>
            </div>
        </div>
    );
}
